import { Box, Flex } from '@chakra-ui/react';
import StatusBox from '../StatusBox/StatusBox.tsx';
import React, { useState, useEffect } from 'react';
import { StatusType } from '../../constants/statusColors.ts';
import statusListData from '../../mocks/change-status-data.json';
import AleaModal from '../AleaModal/AleaModal.tsx';
import { useTranslation } from 'react-i18next';
import AleaStatusBadge from '../AleaStatusBadge/AleaStatusBadge.tsx';

interface AleaChoiceStatusProps {
  stateName: StatusType;
  onStatusChange?: (newStatus: StatusType) => void;
  residentName?: string;
}

const AleaChoiceStatus: React.FC<AleaChoiceStatusProps> = ({
  stateName,
  onStatusChange,
  residentName,
}) => {
  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState<StatusType>(stateName);
  const [tempSelectedStatus, setTempSelectedStatus] =
    useState<StatusType>('Enabled');
  const [isStatusListVisible, setIsStatusListVisible] =
    useState<boolean>(false);
  const [statusList, setStatusList] = useState(statusListData.data);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: t('changeStatus', { residentName: residentName || '' }),
    description: '',
    onConfirm: () => {},
    variant: '',
    titleColor: 'black',
  });

  /**
   * Handles the click event on a status item, sets the temporary status, and opens the confirmation modal.
   *
   * @param {StatusType} tempStatus - The status to be set temporarily.
   */
  const handleStatusClick = (tempStatus: StatusType) => {
    // set temp status
    setTempSelectedStatus(tempStatus);

    // show confirm modal
    setModalOpen(true);
  };

  /**
   * Confirms the status change, updates the selected status and status list, and closes the modal.
   */
  function handleStatusConfirm() {
    setSelectedStatus(tempSelectedStatus);
    updateStatusList(tempSelectedStatus);
    setModalOpen(false);
    setIsStatusListVisible(false);
    onStatusChange?.(tempSelectedStatus);
  }

  /**
   * Updates the status list by marking the selected status as active.
   *
   * @param {StatusType} status - The status to be updated.
   */
  const updateStatusList = (status: StatusType) => {
    const newStatusList = statusList.map((item) => {
      if (item.id === status) {
        return {
          ...item,
          isSelected: true,
        };
      }

      return {
        ...item,
        isSelected: false,
      };
    });

    setStatusList(newStatusList);
  };

  /**
   * Toggles the visibility of the status list.
   */
  const handleStatusBoxClick = () => {
    setIsStatusListVisible(!isStatusListVisible);
  };

  useEffect(() => {
    setModalConfig((prevConfig) => ({
      ...prevConfig,
      onConfirm: handleStatusConfirm,
    }));
  }, [tempSelectedStatus]);

  useEffect(() => {
    if (residentName) {
      setModalConfig((prevConfig) => ({
        ...prevConfig,
        title: t('changeStatus', { residentName: residentName }),
      }));
    }
  }, [residentName, t]);

  return (
    <Flex ml="22px">
      {/*selected status*/}
      <Box onClick={handleStatusBoxClick} mr="5px" cursor="pointer">
        <StatusBox status={selectedStatus} />
      </Box>

      {/*choice status*/}
      {isStatusListVisible && (
        <Flex>
          {/*show status list here except selected status*/}
          {statusList.map((statusItem) => {
            if (statusItem.id !== selectedStatus) {
              return (
                <Box
                  key={statusItem.id}
                  onClick={() => handleStatusClick(statusItem.id as StatusType)}
                  mr="5px"
                  cursor="pointer"
                >
                  <StatusBox status={statusItem.id as StatusType} />
                </Box>
              );
            }

            return null;
          })}
        </Flex>
      )}

      <AleaModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={modalConfig.title}
        description={modalConfig.description}
        onConfirm={modalConfig.onConfirm}
        confirmLabel={t('confirm')}
        cancelLabel={t('cancel')}
        variant="confirm"
        titleColor={modalConfig.titleColor}
      >
        {/*custom content*/}
        <Flex justifyContent="center" mt="10px" mb="12px">
          {/*current status*/}
          <AleaStatusBadge status={selectedStatus} />

          {/*arrow*/}
          <Box ml="10px" mr="10px">
            →
          </Box>

          {/*new status*/}
          <AleaStatusBadge status={tempSelectedStatus} />
        </Flex>
      </AleaModal>
    </Flex>
  );
};

export default AleaChoiceStatus;
